import { useState } from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { useUnsavedChangesPrompt } from 'hooks/useUnsavedChangesPrompt';
import { useFormPristineBaseline } from 'hooks/useFormPristineBaseline';
import { toast } from 'react-toastify';
import { ArrowLeft, Recycle, Warehouse } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import {
    BarcodeType,
    GetDetailedProductQuery,
    GetProductPricesQuery,
    GetProductPriceRangesQuery,
    GetTagsQuery,
    GetActiveCategoriesQuery,
    GetActiveOrganizationsQuery,
    GetSuppliersQuery,
    useUpdateProductMutation,
    ProductUpdateInput,
    GetProductPricesDocument,
    TotadminPermission,
} from 'data/__generated__';

import { PageTitle } from 'components/PageTitle';
import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';

import { DimensionsSection } from './DimensionsSection';
import { GeneralInfoSection } from './GeneralInfoSection';
import { SalesInfoSection } from './SalesInfoSection';
import { StatesSection } from './StatesSection';
import { NutritionalInfoSection } from './NutritionalInfoSection';
import { StockAndSupplierInfoSection } from './StockAndSupplierInfoSection';
import { TagsAndBadgesSection } from './TagsAndBadgesSection';
import { EndOfLifeProductModal } from 'pages/Products/ProductDetails/components/EndOfLifeProductModal';
import { formatFormArray, formatMultilineToArray } from 'pages/Products/utils';
import { ProductGroupSection } from './ProductGroupSection';

import { ProductArrangementsSection } from './ProductArrangementsSection';
import { LocationsSection } from './LocationsSection';
import { ColumnStocksSection } from './ColumnStocksSection';
import { RecipeSection, RecipeIngredient } from './RecipeSection';
import { ProductAuditSection } from './ProductAuditSection';
import { ENV_DEV } from 'constants/environment';
import { useHasPermission } from 'hooks/useHasPermission';
import { WAREHOUSE_PREP_FLOW, WAREHOUSE_SITE_TO_COLUMN_ID } from 'pages/Stocks/constants';

type Merge<A, B> = Omit<A, keyof B> & B;

type FormData = Merge<
    ProductUpdateInput,
    {
        access: { value: string; label: string }[];
        barcodes: { barcode: string; type: BarcodeType }[];
        diets: { value: string; label: string }[];
        preferences: { value: string; label: string }[];
        supplierShortageInfo: {
            inShortage: boolean;
            returnDate: string | null;
        };
        tagOptions: { value: string; label: string }[]; //object type used with UI Select, stocking as strings in DB
        searchKeywords: string | null;
        recipeIngredients: RecipeIngredient[];
    }
>;

export const ProductGlobalForm = ({
    tags,
    organizations,
    detailedProduct,
    productPrices,
    productPriceRanges,
    suppliers,
}: {
    tags: GetTagsQuery['tags'];
    organizations: GetActiveOrganizationsQuery['activeOrganizations'];
    detailedProduct: NonNullable<GetDetailedProductQuery['detailedProduct']>;
    productPrices: GetProductPricesQuery['productPrices'];
    productPriceRanges: GetProductPriceRangesQuery['productPriceRanges'];
    suppliers: GetSuppliersQuery['suppliers'];
}) => {
    const {
        _id,
        displayKg,
        fullname,
        access,
        barcodes,
        diets,
        portion,
        preferences,
        tagIds,
        category,
        suppliers: productSuppliers,
        flow,
    } = detailedProduct;
    const [modalIsOpen, setIsOpen] = useState(false);

    const [updateProduct, { loading: updateLoading }] = useUpdateProductMutation();

    // Sans ces deux droits, le bloc prix n'est pas monté (lecture) ou pas modifiable (écriture) : on n'envoie
    // alors PAS `rangePrices`, que l'API lit comme « prix inchangés ». Envoyer la liste du formulaire la
    // viderait (bloc absent) ou se ferait refuser (pas de `priceRanges_manage`).
    const canReadPrices = useHasPermission(TotadminPermission.PriceRangesRead);
    const canManagePrices = useHasPermission(TotadminPermission.PriceRangesManage);
    const shouldSendPrices = canReadPrices && canManagePrices;

    const openModal = () => {
        setIsOpen(true);
    };

    const methods = useForm<FormData>({
        // need to define defaultValues here in order for useFieldArray to work
        defaultValues: {
            categoryId: category._id,
            subcategoryId: detailedProduct.subcategory?._id ?? null,
            // need to transform string to object because useFieldArray doesn't support strings in arrays
            barcodes,
            access: access.reduce<{ value: string; label: string }[]>((acc, organizationId) => {
                const activeOrganization = organizations.find(({ _id }) => _id === organizationId);
                if (activeOrganization) {
                    acc.push({
                        value: organizationId,
                        label: activeOrganization.name,
                    });
                }

                return acc;
            }, []),
            tagOptions: tagIds.map((tagId) => {
                const tag = tags.find(({ _id }) => _id === tagId);
                return {
                    value: tagId,
                    label: tag?.name || 'Sans nom',
                };
            }),
            preferences: preferences.map(({ _id, name }) => {
                return {
                    value: _id,
                    label: name,
                };
            }),
            diets: diets.map(({ _id, name }) => {
                return {
                    value: _id,
                    label: name,
                };
            }),
            suppliers: productSuppliers,
            recipeIngredients:
                detailedProduct.recipe?.ingredients.map((ingredient) => ({
                    productId: ingredient.productId,
                    quantity: ingredient.quantity,
                    hotDrinksIngredientUnit: ingredient.hotDrinksIngredientUnit,
                })) || [],
        },
    });

    const {
        handleSubmit,
        formState: { isDirty },
    } = methods;

    // les sections portent leurs propres valeurs initiales : sans ça le formulaire est considéré
    // comme modifié dès l'ouverture de la page. Après un enregistrement réussi c'est le remontage
    // du formulaire (`key={detailedProduct.updatedAt}`) qui le remet à l'état propre ; en cas
    // d'erreur métier il n'y a pas de remontage et le formulaire reste modifié, ce qu'on veut.
    useFormPristineBaseline(methods);

    useUnsavedChangesPrompt({
        message: 'Êtes-vous sûr.e de vouloir quitter cette page sans enregistrer ?',
        when: ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
    });

    const onSubmit = handleSubmit(
        async (fields) => {
            fields.isBeingTested = fields.isBeingTested || detailedProduct.isBeingTested;

            const tagIds = formatFormArray(fields.tagOptions);
            const formattedAccess = formatFormArray(fields.access);
            const formattedBarcodes = fields.barcodes.map(({ barcode, type }) => ({ barcode, type }));
            const formattedDiets = formatFormArray(fields.diets);
            const formattedPreferences = formatFormArray(fields.preferences);
            const formattedSearchKeywords = formatMultilineToArray(fields.searchKeywords || '');
            const formattedRecipeIngredients = fields.recipeIngredients;

            const { tagOptions, recipeIngredients, rangePrices, ...fieldsWithoutTags } = fields;

            if (!fields.categoryId) {
                toast.error('Un produit doit être associé à une catégorie.');
                return;
            }

            const { data } = await updateProduct({
                variables: {
                    productId: detailedProduct._id,
                    fields: {
                        ...fieldsWithoutTags,
                        ...(shouldSendPrices ? { rangePrices } : {}),
                        access: formattedAccess,
                        barcodes: formattedBarcodes,
                        diets: formattedDiets,
                        preferences: formattedPreferences,
                        searchKeywords: formattedSearchKeywords,
                        tagIds,
                        state: detailedProduct.state,
                        recipe:
                            formattedRecipeIngredients.length > 0 ? { ingredients: formattedRecipeIngredients } : null,
                    },
                },
                refetchQueries: shouldSendPrices
                    ? [{ query: GetProductPricesDocument, variables: { productId: detailedProduct._id } }]
                    : [],
            });
            if (data) {
                const {
                    updateProduct: { success, errors, formErrors },
                } = data;

                errors?.forEach((error, index) => {
                    toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
                });

                if (success) {
                    toast.success(`Le produit "${fields.name}" a bien été modifié !`);
                } else {
                    if (formErrors) {
                        formErrors.forEach(({ sectionName, sectionErrors }, index) => {
                            toast.error(
                                <span key={index}>
                                    Erreur dans la section "{sectionName}" :
                                    {sectionErrors.map(({ fieldName, fieldError }, index) => (
                                        <span key={index}>
                                            <br />- {fieldName} : "{fieldError}"
                                        </span>
                                    ))}
                                </span>,
                                { autoClose: false },
                            );
                        });

                        if (formErrors.length > 1) {
                            toast.info('Cliquez pour fermer toutes les notifications', {
                                autoClose: false,
                                onClick: () => toast.dismiss(),
                            });
                        }
                    } else {
                        throw Error("Une erreur inconnue s'est produite");
                    }
                }
            } else {
                throw Error("Une erreur inconnue s'est produite");
            }
        },
        (_errors) => {
            toast.error("Le formulaire n'est pas valide. Veuillez corriger les erreurs avant de continuer.");
        },
    );

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Produit',
            items: [
                {
                    label: 'Gérer les produits en fin de vie',
                    icon: <Recycle />,
                    onClick: openModal,
                    disabled: updateLoading,
                    requiredPermissions: [
                        TotadminPermission.ProductsManage,
                        TotadminPermission.CategoriesRead,
                        TotadminPermission.StockVariationsRead,
                        TotadminPermission.ColumnTemplatesRead,
                    ],
                },
                {
                    label: "Voir les stocks dans l'entrepôt",
                    icon: <Warehouse />,
                    requiredPermissions: [TotadminPermission.StockVariationsRead],
                    to: `/column/${WAREHOUSE_SITE_TO_COLUMN_ID[WAREHOUSE_PREP_FLOW[flow]]}/stocks/${_id}`,
                },
            ],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/products' }],
        },
    ];

    return (
        <>
            {modalIsOpen ? (
                <EndOfLifeProductModal
                    fullname={fullname}
                    productId={_id}
                    displayKg={displayKg}
                    categoryId={category._id}
                    portion={portion}
                    setIsOpen={setIsOpen}
                />
            ) : null}
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.productDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={updateLoading}
                            saveTitle="Mettre à jour"
                            saveDataTest="submit-button"
                            // Les prix ne partent qu'avec `priceRanges_manage` (cf. `shouldSendPrices`) : sans ce
                            // droit, l'enregistrement laisse les prix en base intacts.
                            saveRequiredPermissions={[TotadminPermission.ProductsManage]}
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection product={detailedProduct} />
                            <SalesInfoSection
                                product={detailedProduct}
                                productPrices={productPrices}
                                productPriceRanges={productPriceRanges}
                                organizations={organizations}
                            />
                            <TagsAndBadgesSection tags={tags} />
                            <NutritionalInfoSection product={detailedProduct} />
                            <DimensionsSection product={detailedProduct} />
                            <RecipeSection product={detailedProduct} />
                            <StockAndSupplierInfoSection product={detailedProduct} suppliers={suppliers} />
                            <LocationsSection product={detailedProduct} />
                            <ColumnStocksSection productId={detailedProduct._id} />
                            <StatesSection
                                product={detailedProduct}
                                isDirty={isDirty && window.__TOTENV__.ENV !== ENV_DEV}
                            />
                            <ProductGroupSection product={detailedProduct} />
                            <ProductArrangementsSection product={detailedProduct} />
                            <ProductAuditSection
                                product={detailedProduct}
                                productPrices={productPrices}
                                productPriceRanges={productPriceRanges}
                            />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
